<script setup>
import SummaryChart from '@/containers/components/chart/SummaryChart';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useMetricFunction } from './useMetricData';
import { useI18n } from 'vue-i18n';
import { getDefaultMetricPayload } from '@/containers/components/chart/useChart.js';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';
import { EVENT_POPUP } from '@/components/constants/constants';

const props = defineProps({
  metric: {
    type: Object,
    default: () => ({})
  },
  selectedTargets: {
    type: Array,
    default: () => []
  },
  searchModel: {
    type: Object,
    default: () => ({})
  },
  dateOption: {
    type: Object,
    default: () => ({})
  },
  enableLegend: {
    type: Boolean,
    default: false
  },
  collInterval: {
    type: String,
    default: '1m'
  },
  parentClass: {
    type: String,
    default: ''
  },
  fetchStatus: {
    type: Boolean,
    default: false
  },
  isNow: {
    type: Boolean,
    default: false
  },
  showLastValue: {
    type: Boolean,
    default: () => true
  }
});
const emit = defineEmits(['onFetched']);
const dataSummaryChart = ref({});
const showTooltipOfChartList = ref({});

// Use plugin global
const { t } = useI18n();

//Computed
const inquiryTime = computed(() => [
  props.dateOption.queryStartDt,
  props.dateOption.queryEndDt,
  props.dateOption.isNow
]);

const getSummaryOptions = computed(() => {
  const {
    displayUnit,
    metricName,
    metricUnit,
    fixedUnit,
    productTargetType,
    productTargetTypeEn,
    metricDescription,
    metricDescriptionEn
  } = props.metric;
  const productType = props.searchModel.productTypeCode;
  return {
    displayUnit: displayUnit || metricUnit,
    metricName,
    metricUnit,
    fixedUnit,
    productTargetType,
    productTargetTypeEn,
    metricDescription,
    metricDescriptionEn,
    productType
  };
});

const showTooltipOfChart = computed(() =>
  Object.values(showTooltipOfChartList.value).every((showTooltip) => !!showTooltip)
);

// Use Composable
const {
  listIndividuals,
  metricData,
  isK8s,
  isGetObject,
  getObjectList,
  getK8sObjectList,
  getMetricData
} = useMetricFunction(props, inquiryTime);

// Methods
const setDataSummaryChart = (data) => {
  dataSummaryChart.value = {
    ...dataSummaryChart.value,
    data
  };
};
const getInitDataSummaryChart = () => {
  const [startTime, endTime] = inquiryTime.value;
  const allIndividualItems = listIndividuals.value.map(({ value }) => value) || [];
  const { interval, statisticsTypeList } = getDefaultMetricPayload(
    props.searchModel.productTypeCode,
    startTime,
    endTime,
    props.collInterval
  );
  return getDataSummaryChart({
    selectedIndividualItems: allIndividualItems,
    statisticsTypeList,
    startTime,
    endTime,
    interval
  });
};

const getDataSummaryChart = async ({
  selectedIndividualItems = [],
  statisticsTypeList,
  startTime,
  endTime,
  interval
}) => {
  await getMetricData(
    {
      selectedIndividualItems,
      statisticsTypeList,
      startTime,
      endTime,
      interval
    },
    true
  );
  setDataSummaryChart(metricData.value);
};
const onInit = async () => {
  if (isGetObject.value) {
    await getObjectList();
  }
  if (isK8s.value) {
    await getK8sObjectList();
  }
  getInitDataSummaryChart();
};
const setShowTooltipOfChart = (key, value) => {
  showTooltipOfChartList.value[key] = value;
};

watch(
  () => props.fetchStatus,
  async (value) => {
    if (value) {
      await onInit();
      emit('onFetched', 'summary');
    }
  },
  { immediate: true }
);

//Hooks
onMounted(() => {
  for (const EVENT in EVENT_POPUP)
    legoEventBus.$on(EVENT_POPUP[EVENT], (popupShow) => setShowTooltipOfChart(EVENT, !popupShow));
});
onBeforeUnmount(() => {
  for (const EVENT in EVENT_POPUP) legoEventBus.$off(EVENT);
});
</script>
<template>
  <div class="summary-metric__container">
    <SummaryChart
      class="summary-chart-item"
      :summaryData="dataSummaryChart.data"
      :selectedMetric="metric"
      :selectedTargets="selectedTargets"
      :productSubType="searchModel.subTypeCode"
      :productType="searchModel.productTypeCode"
      :inquiryTime="inquiryTime"
      :options="getSummaryOptions"
      :collInterval="collInterval"
      :enableLegend="enableLegend"
      :parentClass="parentClass"
      :showTooltipOfChart="showTooltipOfChart"
      :isNow="isNow"
      :showLastValue="showLastValue"
    />
  </div>
</template>

<style lang="scss" scoped>
.summary-metric__container {
  height: 100%;
}
</style>
